import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
  BrowserRouter as Router
} from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';
import { PostHogProvider } from 'posthog-js/react';
import { PostHogConfig } from 'posthog-js';
import { TooltipProvider } from '@companion-professional/components';
import { Branding } from '@companion-professional/branding';
import '@companion-professional/branding/shepherd/fonts.css';
import { WindowWithVportalSettings } from './types/global';
import { version, commitHash } from './lib/version';
import { App } from './App';
import './tailwind.css';

declare let window: WindowWithVportalSettings;

Sentry.init({
  environment: window.settings.env,
  dsn: window.settings.sentryDsn,
  release: `${version}-${commitHash}`,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false
    }),
    Sentry.reactRouterV6BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes
    })
  ],

  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions

  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/vportal\.(.*\.)?companionpro\.biz/i],

  // Session Replay

  // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a
  // lower rate in production.
  replaysSessionSampleRate: 0.1,

  // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where
  // errors occur.
  replaysOnErrorSampleRate: 1.0
});

// PostHog is used for analytics, event tracking and feature flags.  The following are the options that are passed to
// the PostHogProvider.
const posthogOptions: Partial<PostHogConfig> = {
  api_host: window.settings.posthogHost,

  //  This is to prevent capturing events while developing locally (essentially, this calls the
  //  posthog.opt_out_capturing() method in the PostHogProvider when opt_out_capturing_by_default is set to true).
  opt_out_capturing_by_default: window.settings.env === 'development'
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // I'm not sure having retries is helping us at the moment, so I'm setting the retry to false.  This means that
      // if a query fails, it will not be retried and immediately return an error.
      retry: false
    }
  }
});

// This is the main entry point for the web app. It is responsible for rendering the App component and setting up any
// high level context/providers (like the QueryClientProvider)
ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {/* TODO: Add fallback error component to Sentry.ErrorBoundary */}
    <Sentry.ErrorBoundary>
      <PostHogProvider apiKey={window.settings.posthogPublicKey} options={posthogOptions}>
        <QueryClientProvider client={queryClient}>
          <Router>
            <Branding>
              <TooltipProvider>
                <App />
              </TooltipProvider>
            </Branding>
          </Router>
          <ReactQueryDevtools initialIsOpen={false} />
        </QueryClientProvider>
      </PostHogProvider>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
